/**
 * Operator-facing copy for Generate-from-scope file ingest failures.
 *
 * Next.js server-action misses (`failed-to-find-server-action`) used to
 * leak into the amber banner. Keep those internals out of the UI even
 * if an old client still posts an action ID.
 */

const SERVER_ACTION_NOT_FOUND =
  /Server Action ["“]?[a-f0-9]+["”]? was not found|failed-to-find-server-action/i;

export function isServerActionNotFoundMessage(message: string): boolean {
  return SERVER_ACTION_NOT_FOUND.test(message);
}

export const STALE_CLIENT_SCOPE_MESSAGE =
  "This page is out of date. Refresh, then try again — or paste the text.";

export function sanitizeScopeParseError(message: string): string {
  if (isServerActionNotFoundMessage(message)) {
    return STALE_CLIENT_SCOPE_MESSAGE;
  }
  return message;
}

const OPERATOR_COMPLETE_SCOPE_ERROR =
  /^(No readable text|That DOCX appears|File is over |Only PDF and DOCX|Unsupported file|This page is out of date)/;

export function formatScopeUploadError(err: unknown): string {
  const raw = err instanceof Error ? err.message : "unknown error";
  if (isServerActionNotFoundMessage(raw)) {
    return STALE_CLIENT_SCOPE_MESSAGE;
  }
  const msg = sanitizeScopeParseError(raw);
  if (OPERATOR_COMPLETE_SCOPE_ERROR.test(msg)) return msg;
  return `Couldn't read the file (${msg}). Try pasting the text instead.`;
}

export function formatScopeActionError(err: unknown, fallback: string): string {
  const raw = err instanceof Error ? err.message : fallback;
  return sanitizeScopeParseError(raw);
}

/**
 * Retry in the browser only when the parse route failed for a
 * transport / auth / body-shape reason — not when it already
 * decided the PDF is empty or unsupported.
 */
export function shouldFallbackScopeParseToBrowser(
  status: number,
  error: string,
): boolean {
  if (status === 0 || status === 401 || status === 403 || status === 413) {
    return true;
  }
  if (status >= 500) return true;
  return (
    /no file uploaded/i.test(error) ||
    /could not parse the uploaded file/i.test(error) ||
    /couldn't reach the server/i.test(error) ||
    /couldn't read the file on the server/i.test(error) ||
    /server parse failed/i.test(error)
  );
}
